import { useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid2,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  Fade,
  Stack,
  useTheme
} from "@mui/material"
import {
  Close as CloseIcon,
  PointOfSale as RegisterIcon,
  Settings as SettingsIcon,
  Receipt as ReceiptIcon,
  Save as SaveIcon,
  CurrencyExchange as CurrencyIcon
} from "@mui/icons-material"
import { useForm, Controller } from "react-hook-form"
import {
  useCreateLocationRegisterMutation,
  useUpdateLocationRegisterMutation,
  useGetLocationRegisterQuery,
  LocationRegisterType,
  PrintModeType,
  CurrencyTypeEnum
} from "src/graphql/quark/quark.type"
import OutlinedInput from "src/shared/components/outlined-input"
import InputAutocomplete from "src/shared/components/input-autocomplete"
import { REGISTER_TYPE_OPTIONS, PRINT_MODE_OPTIONS, CURRENCY_OPTIONS } from "../../constants"
import { Conditional } from "src/shared/components"
import { toast } from "react-toastify"
import { useSearchParams } from "react-router-dom"
import { useTypedSelector } from "src/shared/store/store"

type RegisterFormData = {
  name: string
  type: LocationRegisterType
  printMode: PrintModeType
  currency: string
  terminalTaxId: string
  locationId: string
}

const initialValues: RegisterFormData = {
  name: "",
  type: LocationRegisterType.SalesRegister,
  printMode: PrintModeType.Thermal,
  currency: "USD",
  terminalTaxId: "",
  locationId: ""
}

const CreateUpdateRegisterModal = () => {
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()
  const registerId = searchParams.get("id")
  const formMode = searchParams.get("mode")
  const isOpen = !!formMode
  const currentWhLoc = useTypedSelector((state) => state.authentication.currentWhLoc)

  const { data, loading: isGetRegisterLoading } = useGetLocationRegisterQuery({
    variables: {
      getLocationRegisterId: registerId as string,
      locationId: currentWhLoc?.id as string
    },
    skip: !registerId || !currentWhLoc?.id
  })
  const register = data?.getLocationRegister

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty }
  } = useForm<RegisterFormData>({
    defaultValues: initialValues
  })

  const [createRegister, { loading: createLoading }] = useCreateLocationRegisterMutation()
  const [updateRegister, { loading: updateLoading }] = useUpdateLocationRegisterMutation()

  const watchedType = watch("type")
  const watchedPrintMode = watch("printMode")
  const watchedCurrency = watch("currency")

  useEffect(() => {
    if (register && formMode === "edit") {
      reset({
        name: register.name || "",
        type: register.type || LocationRegisterType.SalesRegister,
        printMode: register.printMode || PrintModeType.Thermal,
        currency: register.currency || "USD",
        terminalTaxId: register.terminalTaxId || "",
        locationId: register.location?.id || ""
      })
    } else if (formMode === "create") {
      reset(initialValues)
    }
  }, [register, formMode, reset])

  const onSubmit = async (data: RegisterFormData) => {
    try {
      const registerInput = {
        name: data.name,
        type: data.type,
        printMode: data.printMode,
        currency: data.currency as CurrencyTypeEnum,
        terminalTaxId: data.terminalTaxId,
        locationId: currentWhLoc?.id as string
      }

      if (formMode === "create") {
        const resp = await createRegister({
          variables: { createLocationRegisterDto: registerInput },
          refetchQueries: ["GetLocationRegistersForList"]
        })
        resp.data?.createLocationRegister.id
          ? toast.success("Register created successfully")
          : toast.error("Failed to create register")
      } else {
        const resp = await updateRegister({
          variables: {
            updateLocationRegisterDto: {
              ...registerInput,
              id: register?.id as string
            }
          },
          refetchQueries: ["GetLocationRegistersForList"]
        })
        resp.data?.updateLocationRegister
          ? toast.success("Register updated successfully")
          : toast.error("Failed to update register")
      }
      handleClose()
    } catch (error) {
      console.error("Error saving register:", error)
      toast.error("An error occurred while saving the register")
    }
  }

  const handleClose = () => {
    reset(initialValues)
    setSearchParams({})
  }

  const getTypeIcon = (type: string) => {
    console.log("getTypeIcon-type", type)
    return <RegisterIcon />
  }

  const getTypeColor = (type: string) => {
    switch (type) {
      case LocationRegisterType.SalesRegister:
        return "primary"
      case LocationRegisterType.CartRegister:
        return "success"
      default:
        return "default"
    }
  }

  const getPrintModeIcon = (printMode: string) => {
    console.log("getPrintModeIcon-printMode", printMode)
    return <ReceiptIcon />
  }

  const getPrintModeColor = (printMode: string) => {
    return printMode === PrintModeType.Thermal ? "primary" : "default"
  }

  const getCurrencyIcon = (currency: string) => {
    console.log("getCurrencyIcon-currency", currency)
    return <CurrencyIcon />
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "600px"
        }
      }}
    >
      <Conditional condition={!isGetRegisterLoading} elseBlock={<div>Loading...</div>}>
        <DialogTitle
          sx={{
            background:
              theme.palette.mode === "dark"
                ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: theme.palette.primary.contrastText,
            py: 3,
            px: 4,
            position: "relative"
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {formMode === "create" ? "Create New Register" : "Edit Register"}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {formMode === "create" ? "Configure a new point-of-sale register" : "Update register configuration"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              color: theme.palette.primary.contrastText
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ background: theme.palette.background.default }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid2 container spacing={3} sx={{ py: 4 }}>
              {/* Basic Information Section */}
              <Grid2 size={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <SettingsIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Basic Information
                  </Typography>
                </Box>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12 }}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: "Register name is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Register Name"
                          placeholder="Enter register name"
                          error={!!error}
                          required
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="type"
                      control={control}
                      rules={{ required: "Register type is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.value)
                          }}
                          label="Register Type"
                          options={REGISTER_TYPE_OPTIONS}
                          value={REGISTER_TYPE_OPTIONS.find((option) => option.value === field.value)}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="printMode"
                      control={control}
                      rules={{ required: "Print mode is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.value)
                          }}
                          label="Print Mode"
                          options={PRINT_MODE_OPTIONS}
                          value={PRINT_MODE_OPTIONS.find((option) => option.value === field.value)}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              {/* Configuration Section */}
              <Grid2 size={12}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <RegisterIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Register Configuration
                  </Typography>
                </Box>

                <Grid2 container spacing={2}>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="currency"
                      control={control}
                      rules={{ required: "Currency is required" }}
                      render={({ field, fieldState: { error } }) => (
                        <InputAutocomplete
                          onChange={(option: any) => {
                            field.onChange(option.value)
                          }}
                          label="Currency"
                          options={CURRENCY_OPTIONS}
                          value={CURRENCY_OPTIONS.find((option) => option.value === field.value)}
                          disableClearable={true}
                          required
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>

                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <Controller
                      name="terminalTaxId"
                      control={control}
                      render={({ field, fieldState: { error } }) => (
                        <OutlinedInput
                          {...field}
                          fullWidth
                          label="Terminal Tax ID"
                          placeholder="Enter terminal tax ID"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </Grid2>
                </Grid2>
              </Grid2>

              {/* Current Selection Display */}
              <Grid2 size={12}>
                <Fade in={isDirty}>
                  <Alert
                    severity="info"
                    sx={{
                      borderRadius: 2,
                      backgroundColor:
                        theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                      color: theme.palette.text.primary,
                      "& .MuiAlert-message": { width: "100%" }
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" fontWeight="bold">
                        Current Configuration:
                      </Typography>
                      <Chip
                        icon={getTypeIcon(watchedType)}
                        label={REGISTER_TYPE_OPTIONS.find((opt) => opt.value === watchedType)?.label}
                        color={getTypeColor(watchedType) as any}
                        size="small"
                      />
                      <Chip
                        icon={getPrintModeIcon(watchedPrintMode)}
                        label={PRINT_MODE_OPTIONS.find((opt) => opt.value === watchedPrintMode)?.label}
                        color={getPrintModeColor(watchedPrintMode) as any}
                        size="small"
                      />
                      <Chip icon={getCurrencyIcon(watchedCurrency)} label={watchedCurrency} color="success" size="small" />
                    </Stack>
                  </Alert>
                </Fade>
              </Grid2>
            </Grid2>
          </form>
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            p: 3,
            background: theme.palette.background.default
          }}
        >
          <Button onClick={handleClose} variant="outlined" size="large" sx={{ minWidth: 120 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            size="large"
            disabled={createLoading || updateLoading}
            startIcon={createLoading || updateLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            sx={{
              minWidth: 160,
              background: theme.palette.primary.main,
              "&:hover": {
                background: theme.palette.primary.dark
              }
            }}
          >
            {createLoading || updateLoading
              ? "Saving..."
              : formMode === "create"
                ? "Create Register"
                : "Update Register"}
          </Button>
        </DialogActions>
      </Conditional>
    </Dialog>
  )
}

export default CreateUpdateRegisterModal
